import {
	CreateTicketRequest,
	Ticket,
	TicketAttachmentMeta,
	ticketAttachmentMetaSchema,
	TicketDetail,
	ticketDetailSchema,
	TicketListResponse,
	ticketListResponseSchema,
	TicketReplyResponse,
	ticketReplyResponseSchema,
	ticketSchema,
	TicketStatus,
} from "@signalist/shared-types";

import { api } from "@/redux/api";

/**
 * Customer support tickets (PREC-3933's `tickets` routes). Every endpoint answers 403 unless the
 * caller's company has SUPPORT_TICKETS, so every consumer must `skip` while the flag is off or
 * still loading.
 *
 * The list is fetched in one page (PREC-3937 decision 4): the API supports only paging plus a
 * status filter, so tabs, counts, search and the category filter are derived client-side from that
 * window in `pages/Support/utils/`.
 */
export const SUPPORT_TICKETS_PAGE_SIZE = 100;

export interface GetTicketsArg {
	page?: number;
	limit?: number;
	status?: TicketStatus[];
}

export const ticketsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getTickets: builder.query<TicketListResponse, GetTicketsArg>({
			query: ({ page = 1, limit = SUPPORT_TICKETS_PAGE_SIZE, status }) => ({
				url: "tickets",
				// The DTO accepts a repeated param as well as a comma-separated list; the comma form
				// keeps the cache key stable and the URL short.
				params: { page, limit, ...(status?.length ? { status: status.join(",") } : {}) },
				responseSchema: ticketListResponseSchema,
			}),
			providesTags: ["Tickets"],
		}),
		createTicket: builder.mutation<Ticket, CreateTicketRequest>({
			query: (body) => ({
				url: "tickets",
				method: "POST",
				body,
				responseSchema: ticketSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
		/** One ticket with its visible thread; a foreign ticket is 404 (never 403). */
		getTicket: builder.query<TicketDetail, number>({
			query: (id) => ({ url: `tickets/${id}`, responseSchema: ticketDetailSchema }),
			providesTags: (_result, _error, id) => [{ type: "Tickets", id }],
		}),
		/** A public reply; on a RESOLVED / CLOSED ticket it reopens to WAITING_ON_US. */
		addTicketComment: builder.mutation<TicketReplyResponse, { ticketId: number; message: string }>({
			query: ({ ticketId, message }) => ({
				url: `tickets/${ticketId}/comments`,
				method: "POST",
				body: { message },
				responseSchema: ticketReplyResponseSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
		/** RESOLVED → CLOSED; any other status answers 400 TICKET_NOT_RESOLVED. */
		confirmTicketResolved: builder.mutation<Ticket, number>({
			query: (ticketId) => ({
				url: `tickets/${ticketId}/confirm-resolved`,
				method: "POST",
				responseSchema: ticketSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
		/**
		 * The bytes of one attachment. The route needs the JWT, so a plain link cannot open it; the
		 * consumer turns the blob into an object URL and revokes it (PREC-3938 decision 1).
		 */
		getTicketAttachmentBlob: builder.query<Blob, { ticketId: number; attachmentId: number }>({
			query: ({ ticketId, attachmentId }) => ({
				url: `tickets/${ticketId}/attachments/${attachmentId}`,
				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		/**
		 * One request per file: the API needs the ticket id before anything can be attached. With a
		 * `commentId` the file lands on that comment (the uploader's own MESSAGE row, PREC-3935).
		 */
		uploadTicketAttachment: builder.mutation<
			TicketAttachmentMeta,
			{ ticketId: number; file: File; commentId?: number }
		>({
			query: ({ ticketId, file, commentId }) => {
				const formData = new FormData();

				formData.append("file", file);

				if (commentId !== undefined) {
					formData.append("commentId", String(commentId));
				}

				return {
					url: `tickets/${ticketId}/attachments`,
					method: "POST",
					body: formData,
					responseSchema: ticketAttachmentMetaSchema,
				};
			},
			invalidatesTags: ["Tickets"],
		}),
	}),
});

export const {
	useGetTicketsQuery,
	useGetTicketQuery,
	useCreateTicketMutation,
	useAddTicketCommentMutation,
	useConfirmTicketResolvedMutation,
	useGetTicketAttachmentBlobQuery,
	useLazyGetTicketAttachmentBlobQuery,
	useUploadTicketAttachmentMutation,
} = ticketsApi;
